import React, { useState, useEffect } from 'react';
import removeMarkdown from 'remove-markdown';
import { TZDate } from '@date-fns/tz';
import { useTranslation } from 'react-i18next';
import { Switch } from '@headlessui/react';
import { SlideOver } from '@/components/design-system/molecules/slide-over';
import { Button } from '@/components/design-system/atoms/button';
import { TextInput } from '@/components/design-system/atoms/text/text-input';
import { DatePicker } from '@/components/design-system/molecules/datepicker';
import { MessageInput } from '@/components/design-system/molecules/message-input';
import { useUpdateBroadcastMessage } from '../../hooks/use-update-broadcast-message';
import { usePopUpConfirmation } from '@/features/shared/pop-up-confirmation/hooks';
import { BroadcastMessage } from '../../types';
import { ImageUpload } from '@/components/design-system/molecules/image-upload';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { NavigationTypeSelector } from '../navigation-type-selector';
import { RadioGroup } from '@/components/design-system/molecules/radio-group';
import { HeroIcons } from '@/components/design-system/atoms/icon';
import { composeClassName } from '@/utils/util_helpers';
import { BROADCAST_MESSAGE_CHARACTER_LIMIT } from '../../constants';

interface Colocation {
  id: number;
  name: string;
  slug: string;
  property_manager: {
    name: string;
  };
}

type EditBroadcastSlideOverProps = {
  show: boolean;
  onClose: () => void;
  broadcastMessage: BroadcastMessage | null;
  colocations: Colocation[];
  onSuccess: () => void;
};

const BROADCAST_TIMING_OPTIONS = [
  { id: 'immediately', label: 'Immediately' },
  { id: 'scheduled', label: 'Scheduled' },
];

const EditBroadcastSlideOver: React.FC<EditBroadcastSlideOverProps> = ({
  show,
  onClose,
  broadcastMessage,
  colocations,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { updateMessage, isLoading, error } = useUpdateBroadcastMessage();
  const { openPopUpConfirmation } = usePopUpConfirmation();
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date(Date.now() + 2 * 24 * 60 * 60 * 1000));
  const [endTime, setEndTime] = useState(new Date(Date.now() + 2 * 24 * 60 * 60 * 1000));
  const [isImageUpdated, setIsImageUpdated] = useState(false);
  const [selectedColocations, setSelectedColocations] = useState<(Colocation | 'all')[]>(['all']);
  const [isAllSelected, setIsAllSelected] = useState(true);
  const [broadcastTiming, setBroadcastTiming] = useState<'immediately' | 'scheduled'>('scheduled');
  const [query, setQuery] = useState('');
  const [navigationType, setNavigationType] = useState<'link' | 'in_app_page' | 'none'>('link');
  const [url, setUrl] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [allowToDismissMessage, setAllowToDismissMessage] = useState(false);

  const isOverLimit = removeMarkdown(message).trim().length >= BROADCAST_MESSAGE_CHARACTER_LIMIT;

  useEffect(() => {
    if (broadcastMessage) {
      const startsAt = new Date(broadcastMessage.starts_at);
      const endsAt = new Date(broadcastMessage.ends_at);
      setImage(null);
      setImageUrl((broadcastMessage.image_url as string) || undefined);
      setTitle(broadcastMessage.title);
      setMessage(broadcastMessage.message);
      setStartDate(startsAt);
      setStartTime(startsAt);
      setEndDate(endsAt);
      setEndTime(endsAt);
      setBroadcastTiming(broadcastMessage.broadcast_timing);
      setNavigationType(broadcastMessage.click_action);
      setUrl(broadcastMessage.button_url);
      setButtonLabel(broadcastMessage.button_label);
      setAllowToDismissMessage(broadcastMessage.allow_to_dismiss_message);
      if (broadcastMessage.all_colocations) {
        setSelectedColocations(['all', ...colocations]);
        setIsAllSelected(true);
      } else {
        const broadcastMessageColocationIds = broadcastMessage.colocations.map(
          (colocation) => colocation.id
        );
        const copySelectedColocations = colocations.filter((colocation) =>
          broadcastMessageColocationIds.indexOf(colocation.id) !== -1
        );
        setSelectedColocations([...copySelectedColocations]);
        setIsAllSelected(false);
      }
    }
  }, [broadcastMessage, colocations]);

  const handleSubmit = () => {
    if (!broadcastMessage) return;

    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startsAt = new TZDate(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startTime.getHours(),
      startTime.getMinutes(),
      localTimezone
    );
    const endsAt = new TZDate(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endTime.getHours(),
      endTime.getMinutes(),
      localTimezone
    );

    const formDataToSend = new FormData();
    formDataToSend.append('broadcast_message[title]', title);
    formDataToSend.append('broadcast_message[message]', message);
    formDataToSend.append('broadcast_message[starts_at]', startsAt.toString());
    formDataToSend.append('broadcast_message[ends_at]', endsAt.toString());
    formDataToSend.append('broadcast_message[broadcast_timing]', broadcastTiming);
    formDataToSend.append('broadcast_message[click_action]', navigationType);
    formDataToSend.append('broadcast_message[button_label]', buttonLabel);
    formDataToSend.append('broadcast_message[button_url]', url);
    formDataToSend.append(
      'broadcast_message[allow_to_dismiss_message]',
      allowToDismissMessage.toString()
    );

    if (isImageUpdated) {
      formDataToSend.append('broadcast_message[image]', image || 'null');
    }

    formDataToSend.append('broadcast_message[all_colocations]', isAllSelected.toString());
    if (!isAllSelected && selectedColocations.length > 0) {
      selectedColocations
        .filter((colocation): colocation is Colocation => typeof colocation !== 'string')
        .forEach((colocation) => {
          formDataToSend.append('broadcast_message[colocation_ids][]', colocation.id.toString());
        });
    }

    updateMessage(
      {
        id: broadcastMessage.id,
        data: formDataToSend,
      },
      {
        onSuccess: async () => {
          await onClose();
          onSuccess();
          openPopUpConfirmation({
            type: 'feedback',
            title: t('js.features.admin.broadcast_messages.updated_successfully'),
            confirmLabel: t('js.features.admin.broadcast_messages.done'),
          });
        },
      }
    );
  };

  const filteredColocations =
    query === ''
      ? colocations
      : colocations.filter((colocation) => {
          return colocation.name.toLowerCase().includes(query.toLowerCase());
        });

  const deleteColocation = (colocation: Colocation | 'all') => {
    const colocationsCopy = [...selectedColocations];
    setSelectedColocations(colocationsCopy.filter((value) => value !== colocation));
  };

  const handleColocationChange = (selected: (Colocation | 'all')[]) => {
    const wasAllSelected = selectedColocations.some((item) => item === 'all');
    const currentIsAllSelected = selected.some((item) => item === 'all');

    if (!wasAllSelected && currentIsAllSelected) {
      setSelectedColocations(['all', ...colocations]);
      setIsAllSelected(true);
    } else if (wasAllSelected && !currentIsAllSelected) {
      setSelectedColocations([]);
      setIsAllSelected(false);
    } else if (wasAllSelected && selected.length < selectedColocations.length) {
      const newSelected = selected.filter(item => item !== 'all' && typeof item !== 'string');
      setSelectedColocations(newSelected);
      setIsAllSelected(false);
    } else {
      setSelectedColocations(selected);
      setIsAllSelected(false);
    }
  };

  return (
    <SlideOver
      show={show}
      title={t('js.features.admin.broadcast_messages.edit_broadcast_message')}
      onClose={onClose}
      className="!z-50"
    >
      <SlideOver.SlideOverContent className="relative flex h-full flex-col">
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto pb-[10rem]">
          {!!error && <div className="text-red-600 text-sm">{error instanceof Error ? error.message : 'Something went wrong'}</div>}

          <ImageUpload
            label={t('js.features.admin.broadcast_messages.form.image')}
            onImageUpload={(file) => {
              setImage(file);
              setImageUrl(undefined);
              setIsImageUpdated(true);
            }}
            onRemoveImage={() => {
              setImage(null);
              setImageUrl(undefined);
              setIsImageUpdated(true);
            }}
            imageUrl={imageUrl}
            maxSize={5242880}
            acceptedFileTypes={['image/png', 'image/svg', 'image/jpg', 'image/jpeg']}
          />

          <TextInput
            label={t('js.features.admin.broadcast_messages.form.title')}
            placeholder={t('js.features.admin.broadcast_messages.form.enter_message_title')}
            required
            value={title}
            className="rounded-[12px] bg-white hover:bg-white"
            onChange={(e) => setTitle(e.target.value)}
          />

          <MessageInput
            label={t('js.features.admin.broadcast_messages.form.message')}
            message={message}
            characterLimit={BROADCAST_MESSAGE_CHARACTER_LIMIT}
            onChange={(value) => setMessage(value)}
          />

          <div className="flex w-full flex-col gap-2">
            <label
              htmlFor="colocations"
              className="break-words text-footnote1 capitalize leading-[16px] text-N700 text-text-secondary"
            >
              {t('js.features.admin.broadcast_messages.form.houses')}
            </label>
            <DropDown
              label={t('js.features.admin.broadcast_messages.form.houses')}
              className="w-full"
              value={selectedColocations}
              multiple
              onChange={handleColocationChange}
              onTextInputChange={(event) => setQuery(event.target.value)}
              inputWrapperClassName="bg-white"
            >
              <DropDown.DropDownInputMultipleValues>
                {!isAllSelected &&
                  selectedColocations
                    .filter((colocation) => colocation !== 'all')
                    .map((colocation) => {
                      return (
                        <DropDown.DropDownInputMultipleValueDefaultContent
                          key={typeof colocation === 'string' ? colocation : colocation.id}
                          onClear={() => deleteColocation(colocation)}
                        >
                          {colocation.name}
                        </DropDown.DropDownInputMultipleValueDefaultContent>
                      );
                    })}

                {isAllSelected && (
                  <DropDown.DropDownInputMultipleValueDefaultContent>
                    {t('js.features.admin.broadcast_messages.form.all_houses')}
                  </DropDown.DropDownInputMultipleValueDefaultContent>
                )}
              </DropDown.DropDownInputMultipleValues>
              <DropDown.DropDownOptions>
                <DropDown.DropDownOption value="all">
                  <DropDown.DropDownOptionDefaultContent>
                    {t('js.features.admin.broadcast_messages.form.all_houses')}
                  </DropDown.DropDownOptionDefaultContent>
                </DropDown.DropDownOption>
                {filteredColocations.map((colocation) => (
                  <DropDown.DropDownOption key={colocation.id} value={colocation}>
                    <DropDown.DropDownOptionDefaultContent>{colocation.name}</DropDown.DropDownOptionDefaultContent>
                  </DropDown.DropDownOption>
                ))}
              </DropDown.DropDownOptions>
            </DropDown>
          </div>

          <div className="flex flex-col gap-2 py-3">
            <div className="h-[1px] w-full bg-border-primaryLight" />
          </div>

          <span className="text-body font-bold text-text-primaryHeavy">
            {t('js.features.admin.broadcast_messages.form.broadcast_type')}
          </span>

          <RadioGroup
            options={BROADCAST_TIMING_OPTIONS}
            value={broadcastTiming}
            onChange={(v) => setBroadcastTiming(v as 'immediately' | 'scheduled')}
          />

          {broadcastTiming === 'scheduled' ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <DatePicker
                    label={t('js.features.admin.broadcast_messages.form.start_date')}
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    type="date"
                    inputClassName="rounded-[12px] bg-white hover:bg-white"
                  />
                </div>
                <div>
                  <DatePicker
                    label={t('js.features.admin.broadcast_messages.form.start_time')}
                    value={startTime}
                    onChange={(time) => setStartTime(time)}
                    type="time"
                    inputClassName="rounded-[12px] bg-white hover:bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <DatePicker
                    label={t('js.features.admin.broadcast_messages.form.end_date')}
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    type="date"
                    inputClassName="rounded-[12px] bg-white hover:bg-white"
                  />
                </div>
                <div>
                  <DatePicker
                    label={t('js.features.admin.broadcast_messages.form.end_time')}
                    value={endTime}
                    onChange={(time) => setEndTime(time)}
                    type="time"
                    inputClassName="rounded-[12px] bg-white hover:bg-white"
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="mt-2 flex flex-row items-start gap-2 rounded-[8px] border border-solid border-border-primaryLight bg-bg-primaryMedium p-[12px]">
              <HeroIcons name="information-circle" size={18} />
              <p className="text-body font-medium text-text-primaryHeavy">
                Selecting 'Immediately' means your announcement will be displayed as soon as it is
                created and saved.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 py-3">
            <div className="h-[1px] w-full bg-border-primaryLight" />
          </div>

          <NavigationTypeSelector
            selectedType={navigationType}
            onTypeChange={setNavigationType}
            urlValue={url}
            onUrlChange={setUrl}
            buttonLabel={buttonLabel}
            onButtonLabelChange={setButtonLabel}
            disabled={isOverLimit}
          />

          <div className="flex flex-col gap-2 py-3">
            <div className="h-[1px] w-full bg-border-primaryLight" />
          </div>

          <div className="flex flex-row items-center justify-between gap-2 pr-3">
            <span className="text-body font-bold text-text-primaryHeavy">
              {t('js.features.admin.broadcast_messages.form.allow_to_dismiss_message')}
            </span>
            <Switch
              checked={allowToDismissMessage}
              onChange={() => {
                setAllowToDismissMessage(!allowToDismissMessage);
              }}
              className="group inline-flex h-5 w-[51px] items-center rounded-full bg-[#E9ECEF] transition data-[checked]:bg-bg-primaryAccentHeavy"
            >
              {({ checked }) => (
                <button
                  type="button"
                  className={composeClassName(
                    'group inline-flex h-5 w-[51px] items-center rounded-full',
                    checked ? 'bg-bg-primaryAccentHeavy' : 'bg-[#E9ECEF]'
                  )}
                >
                  <span className="sr-only">Enable notifications</span>
                  <span
                    className={composeClassName(
                      'size-4 rounded-full bg-white transition',
                      checked ? 'translate-x-[24px]' : 'translate-x-1'
                    )}
                  />
                </button>
              )}
            </Switch>
          </div>
        </div>
        <div className="absolute bottom-[72px] left-0 right-0 border-t bg-white p-4 backdrop-blur-[6px]">
          <Button variant="primary" className="w-full" onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? t('js.features.admin.broadcast_messages.updating')
              : t('js.features.admin.broadcast_messages.update_message')}
          </Button>
        </div>
      </SlideOver.SlideOverContent>
    </SlideOver>
  );
};

export { EditBroadcastSlideOver };
